import React, { useState, useRef } from "react";
import { Modal, Button, Typography, Space, message, Tooltip } from "antd";
import {
  DownloadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  // eslint-disable-next-line no-unused-vars
  FileTextOutlined,
  ReloadOutlined,
  RotateRightOutlined,
} from "@ant-design/icons";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import ScormPreviewTest2 from "./legacyComp/tetsing/ScormPreviewTest2";
import { SCORM_PREVIEW } from "../config";
import ReactPlayer from "react-player";
import ReactH5AudioPlayer from "react-h5-audio-player";
import "react-h5-audio-player/lib/styles.css";

const { Title, Text } = Typography;

const FilePreviewModal = ({
  open,
  onClose,
  fileUrl,
  selectedRecord,
  showName = true,
}) => {
  const [zoomPercent, setZoomPercent] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isReadyToRender, setIsReadyToRender] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [playing, setPlaying] = useState(false);

  const contentRef = useRef(null);
  const audioPlayerRef = useRef(null); // NEW: ref for audio player

  const fileName = fileUrl?.split("/")?.pop() || "Unknown File";
  const fileTypeRaw = fileName?.split(".")?.pop() || "";
  const fileType = fileTypeRaw.toLowerCase();

  // Check if URL is a Vimeo URL
  const isVimeoUrl = (url) => {
    if (!url) return false;
    return /^(https?:\/\/)?(www\.)?(vimeo\.com\/\d+|player\.vimeo\.com\/video\/\d+)/i.test(url) || /^\d+$/.test(url) || selectedRecord?.type === "VIMEO";
  };

  // Normalize Vimeo URL for ReactPlayer
  const normalizeVimeoUrl = (url) => {
    if (!url) return url;
    const videoId = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/i)?.[1] || url.match(/^(\d+)$/)?.[1] || url.match(/(\d+)/)?.[1];
    if (videoId) return `https://vimeo.com/${videoId}`;
    if (!url.startsWith("http")) return `https://${url}`;
    return url;
  };

  const videoUrl = isVimeoUrl(fileUrl) ? normalizeVimeoUrl(fileUrl) : fileUrl;

  // eslint-disable-next-line no-unused-vars
  const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(fileType);
  const isVideo = ["mp4", "webm", "ogg"].includes(fileType) || isVimeoUrl(fileUrl);
  const isAudio = ["mp3", "wav", "m4a", "ogg", "oga", "aac"].includes(fileType);

  const handleDownload = () => {
    try {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      message.error("Failed to download file.");
    }
  };

  const docs = [
    {
      uri: fileUrl,
      fileType,
      fileName,
    },
  ];

  const docViewerKey = `${fileUrl}-${zoomPercent}-${rotation}`;

  const stopAllMediaInModal = () => {
    // Pause the audio player using its ref
    if (audioPlayerRef.current && audioPlayerRef.current.audio.current) {
      audioPlayerRef.current.audio.current.pause();
      audioPlayerRef.current.audio.current.currentTime = 0;
    }

    // Pause any native audio/video tags inside the modal content
    try {
      if (contentRef.current) {
        const mediaEls = contentRef.current.querySelectorAll("audio");
        mediaEls.forEach((el) => {
          try {
            el.pause();
            el.currentTime = 0;
          } catch (e) {
            // ignore per-element errors
          }
        });
      }
    } catch (e) {
      // ignore
    }

    // Reset playing state
    setPlaying(false);
  };

  const handleModalClose = () => {
    stopAllMediaInModal();
    setZoomPercent(100);
    setRotation(0);
    setHasError(false);
    setIsReadyToRender(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleModalClose}
      afterOpenChange={(open) => {
        if (open) {
          setTimeout(() => setIsReadyToRender(true), 100);
        } else {
          stopAllMediaInModal();
        }
      }}
      footer={null}
      width="60%"
      centered
      bodyStyle={{ padding: "16px 24px", height: "80vh" }}
    >
      <div ref={contentRef} style={{ height: "100%" }}>
        {/* Header */}
        <Space
          direction="vertical"
          size="small"
          style={{ width: "100%", marginBottom: 16 }}
        >
          {showName && (
            <Title level={5} style={{ margin: 0 }}>
              {fileName}
            </Title>
          )}
          <Text type="secondary">
            Type: {fileTypeRaw?.toUpperCase() || "N/A"}
          </Text>
        </Space>

        {/* Viewer */}
        {fileType === "xml" || selectedRecord?.type === "SCORM" ? (
          <ScormPreviewTest2
            course_id={selectedRecord?.id}
            apiRoot={SCORM_PREVIEW}
            link={`${SCORM_PREVIEW}${fileUrl.replace(
              /imsmanifest(\.xml)?$/i,
              ""
            )}`}
          />
        ) : isVideo ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "calc(100% - 80px)", minHeight: "400px" }}>
            {isVimeoUrl(fileUrl) ? (
              <iframe
                src={`https://player.vimeo.com/video/${videoUrl.match(/\d+/)?.[0]}?controls=1&autoplay=0`}
                width="100%"
                height="100%"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                style={{ minHeight: "400px", border: "none" }}
                title="Vimeo video player"
              />
            ) : (
              <ReactPlayer 
                url={videoUrl} 
                controls 
                width="100%" 
                height="100%"
                playing={false}
                muted={false}
                volume={1}
              />
            )}
          </div>
        ) : isAudio ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "calc(100% - 80px)",
            }}
          >
            <ReactH5AudioPlayer
              ref={audioPlayerRef}
              src={fileUrl}
              autoPlay={false}
              onError={(e) => {
                console.error("Audio error:", e);
                message.error("Failed to play audio.");
                setHasError(true);
              }}
              onPlay={() => setHasError(false)}
              onPause={() => setPlaying(false)}
              showJumpControls={false}
              style={{ width: "80%", maxWidth: 600 }}
            />
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <Text type="secondary">
                If playback fails, try downloading the file.
              </Text>
              <div style={{ marginTop: 8 }}>
                <Tooltip title="Download">
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleDownload}
                  >
                    Download
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              style={{
                height: "calc(100% - 120px)",
                overflow: "auto",
                border: "1px solid #d9d9d9",
                borderRadius: "4px",
                marginBottom: 16,
                transform: `scale(${zoomPercent / 100}) rotate(${rotation}deg)`,
                transformOrigin: "center center",
                transition: "transform 0.2s ease-in-out",
              }}
            >
              {!hasError && fileUrl && isReadyToRender ? (
                <div style={{ width: "100%", height: "100%" }}>
                  <DocViewer
                    key={docViewerKey}
                    documents={docs}
                    pluginRenderers={DocViewerRenderers}
                    config={{
                      header: {
                        disableHeader: true,
                      },
                    }}
                    onError={() => {
                      setHasError(true);
                      message.error("Failed to load the document.");
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <Text>Loading preview...</Text>
                </div>
              )}
            </div>

            {/* Footer controls */}
            <Space style={{ justifyContent: "space-between", width: "100%" }}>
              <Space>
                <Tooltip title="Zoom Out">
                  <Button
                    icon={<ZoomOutOutlined />}
                    onClick={() => setZoomPercent((z) => Math.max(z - 10, 50))}
                  />
                </Tooltip>
                <Tooltip title="Zoom In">
                  <Button
                    icon={<ZoomInOutlined />}
                    onClick={() => setZoomPercent((z) => Math.min(z + 10, 200))}
                  />
                </Tooltip>
                <Tooltip title="Reset">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      setZoomPercent(100);
                      setRotation(0);
                    }}
                  />
                </Tooltip>
                <Tooltip title="Rotate">
                  <Button
                    icon={<RotateRightOutlined />}
                    onClick={() => setRotation((r) => (r + 90) % 360)}
                  />
                </Tooltip>
                <Text>Zoom: {zoomPercent}%</Text>
              </Space>
              <Tooltip title="Download">
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                >
                  Download
                </Button>
              </Tooltip>
            </Space>
          </>
        )}
      </div>
    </Modal>
  );
};

export default FilePreviewModal;
